import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { offlineStorageService } from '@/services/offlineStorageService';
import { useToast } from './use-toast';

interface Driver {
  id: string;
  name: string;
  cpf: string;
  company_id: string;
  vehicle_id?: string;
}

interface DriverAuthContextType {
  driver: Driver | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (cpf: string, pin: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const DriverAuthContext = createContext<DriverAuthContextType | undefined>(undefined);

interface DriverAuthProviderProps {
  children: ReactNode;
}

export function DriverAuthProvider({ children }: DriverAuthProviderProps) {
  const [driver, setDriver] = useState<Driver | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  async function checkAuthStatus() {
    try {
      const auth = await offlineStorageService.getDriverAuth();
      if (auth) {
        const { data, error } = await supabase
          .from('fleet_drivers')
          .select('id, name, cpf, company_id')
          .eq('id', auth.driverId)
          .eq('status', 'active')
          .single();

        if (data && !error) {
          setDriver(data);
        } else {
          await offlineStorageService.clearDriverAuth();
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function login(cpf: string, pin: string): Promise<boolean> {
    try {
      setIsLoading(true);
      
      const cleanCpf = cpf.replace(/\D/g, '');
      
      // Chamar função RPC segura para validação de login
      const { data, error } = await supabase.rpc('validate_driver_login', {
        cpf_input: cleanCpf,
        pin_input: pin
      });

      if (error) {
        toast({
          title: 'Erro ao fazer login',
          description: error.message,
          variant: 'destructive'
        });
        return false;
      }

      if (!data || data.length === 0) {
        toast({
          title: 'Erro ao fazer login',
          description: 'Dados inválidos retornados',
          variant: 'destructive'
        });
        return false;
      }

      const driverData = data[0];

      await offlineStorageService.saveDriverAuth(driverData.id, 'token_placeholder');
      
      setDriver(driverData);
      
      toast({
        title: 'Login realizado',
        description: `Bem-vindo, ${driverData.name}!`
      });
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Erro ao fazer login',
        description: 'Ocorreu um erro. Tente novamente.',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    await offlineStorageService.clearDriverAuth();
    setDriver(null);
    toast({
      title: 'Logout realizado',
      description: 'Até logo!'
    });
  }

  const value = {
    driver,
    isLoading,
    isAuthenticated: !!driver,
    login,
    logout
  };

  return (
    <DriverAuthContext.Provider value={value}>
      {children}
    </DriverAuthContext.Provider>
  );
}

export function useDriverAuth() {
  const context = useContext(DriverAuthContext);
  if (context === undefined) {
    throw new Error('useDriverAuth must be used within a DriverAuthProvider');
  }
  return context;
}
